import { View } from "react-native";
import { ICall } from "../types/call";
import StyledText from "./common/ui/styled-text";
import { formatPhoneNumber } from "react-phone-number-input";
import { displayPrice } from "../utils/price-helpers";
import { Badge } from "./badge";
import { Pressable } from "react-native";
import PlayAudioButton from "./play-audio-button";
import { useAudioPlayer } from "../contexts/AudioPlayerContext";
import { useFormattedDate } from "../hooks/useFormattedDate";
import {
  isCallCompleted,
  isCallProcessing,
  isPrepaid,
  hasBeenRefunded,
  isExpired,
  isVoided,
  isCallWithOrder,
} from "../utils/call-helpers";
import { Image } from "react-native";
import {
  Ban,
  Calendar,
  ChevronDown,
  ChevronRight,
  CircleCheck,
  CircleX,
  Flag,
  HandCoins,
  Loader2,
  MapPin,
  ShoppingBag,
  Truck,
  Zap,
} from "lucide-react-native";
import { useState } from "react";
import {
  OrderCallIconWithStatus,
  PhoneCallIcon,
  ProcessingCallIcon,
} from "./call-display-icons";

const liveIcon = require("../assets/live.svg");

interface CallCardProps {
  call: ICall;
  isExpanded: boolean;
  onToggle: () => void;
  onViewHistory: () => void;
  onReportCall: () => void;
  onCallBack: () => void;
  onRefund: () => void;
  isStreamOrdering: boolean;
  onBlockCaller: (phone: string) => void;
  onUnblockCaller: (phone: string) => void;
  isCallerBlocked: (phone: string) => boolean;
  isBlockUnblockLoading: (phone: string) => boolean;
}

const CallCard = ({
  call,
  isExpanded,
  onToggle,
  onViewHistory,
  onReportCall,
  onCallBack,
  onRefund,
  isStreamOrdering,
  onBlockCaller,
  onUnblockCaller,
  isCallerBlocked,
  isBlockUnblockLoading,
}: CallCardProps) => {
  const { formatTime, formatDateShort, formatDateTime } = useFormattedDate();
  const hasOrder = call.order != null;
  const orderId = call.order?.id.slice(-4).toUpperCase();
  const isDelivery = call.order?.deliveryAddress != null;
  const isScheduled = call.order?.futureOrderDate != null;

  const { handlePlayPause, soundId, isPlaying, loadingId } = useAudioPlayer();

  const getFulfillmentBadge = () => {
    if (isDelivery) {
      return (
        <Badge
          variant="outline"
          className="bg-purple-50 text-purple-700 border-purple-200 gap-1"
        >
          <Truck size={12} />
          Delivery
        </Badge>
      );
    }
    return (
      <Badge
        variant="outline"
        className="bg-blue-50 text-blue-700 border-blue-200 gap-1"
      >
        <ShoppingBag size={12} />
        Pickup
      </Badge>
    );
  };

  const getTimingBadge = (isExpanded: boolean = false) => {
    if (isScheduled) {
      return (
        <Badge
          variant="outline"
          className="bg-white text-gray-700 border-gray-200 gap-1"
        >
          <Calendar size={12} />
          Scheduled
          {call.order?.futureOrderDate != null && !isExpanded && (
            <span>for {formatDateTime(call.order?.futureOrderDate)}</span>
          )}
        </Badge>
      );
    }
    return (
      <Badge
        variant="outline"
        className="bg-amber-50 text-amber-700 border-amber-200 gap-1"
      >
        <Zap size={12} />
        ASAP
      </Badge>
    );
  };

  const formattedDeliveryAddress = call.order?.deliveryAddress
    ? `${call.order.deliveryAddress.addressLine1}${
        call.order.deliveryAddress.addressLine2
          ? `, ${call.order.deliveryAddress.addressLine2}`
          : ""
      }, ${call.order.deliveryAddress.city}`
    : "";

  const renderModifiers = (modifiers: any[], level: number = 0) => {
    if (!modifiers || modifiers.length === 0) return null;

    return (
      <View className={`${level === 0 ? "mt-1" : "mt-0"}`}>
        {modifiers.map((modifier, modifierIndex) => (
          <View key={modifierIndex}>
            <StyledText
              className="text-xs text-green-700"
              style={{ marginLeft: level * 16 }}
            >
              - {modifier.quantity > 1 ? `${modifier.quantity}x ` : ""}
              {modifier.name}
            </StyledText>
            {renderModifiers(modifier.modifiers, level + 1)}
          </View>
        ))}
      </View>
    );
  };

  return (
    <View
      className={`mb-4 bg-card text-card-foreground flex flex-col gap-6 rounded-lg border py-0 overflow-hidden shadow-sm transition-all duration-200 ${
        isExpanded && hasOrder
          ? "border-green-700"
          : isExpanded && !hasOrder
            ? "border-gray-700"
            : hasOrder
              ? "border-green-200"
              : "border-gray-100"
      }`}
    >
      <View className="grid auto-rows-min grid-rows-[auto_auto] items-start p-0">
        <div className="p-3">
          <Pressable
            onPress={onToggle}
            disabled={!isCallCompleted(call) && !isExpanded}
          >
            <View className="flex flex-row items-center justify-between">
              <div className="flex items-start gap-3">
                <div className="mt-1 flex flex-col items-center gap-1">
                  {isCallProcessing(call, isStreamOrdering) ? (
                    <ProcessingCallIcon />
                  ) : isCallWithOrder(call) ? (
                    <OrderCallIconWithStatus call={call} />
                  ) : (
                    <PhoneCallIcon call={call} />
                  )}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-medium">
                      {call.customerName || formatPhoneNumber(call.phone)}
                    </h3>
                    {call.customerName == null &&
                      isCallerBlocked(call.phone) && (
                        <Ban size={14} color="#ef4444" />
                      )}
                  </div>
                  {!!call.customerName && (
                    <div className="flex items-center gap-1.5">
                      <p className="text-xs text-muted-foreground/70">
                        {formatPhoneNumber(call.phone)}
                      </p>
                      {isCallerBlocked(call.phone) && (
                        <Ban size={12} color="#ef4444" />
                      )}
                    </div>
                  )}
                </div>
              </div>

              {isCallCompleted(call) ? (
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <div className="text-sm text-muted-foreground">
                      <span>{formatTime(call.timestamp)}</span>
                    </div>
                    <div className="text-xs text-muted-foreground/70">
                      {formatDateShort(call.timestamp)}
                    </div>
                  </div>
                  {isExpanded ? (
                    <ChevronDown size={16} color="#71717a" />
                  ) : (
                    <ChevronRight size={16} color="#71717a" />
                  )}
                </div>
              ) : (
                <div className="h-9 flex items-center gap-1 animate-pulse">
                  <p className="font-medium text-sm text-blue-600">
                    Call is ongoing
                  </p>
                  <Image
                    source={liveIcon}
                    className="w-5 h-5"
                    style={{
                      width: 20,
                      height: 20,
                      tintColor: "#2563eb",
                    }}
                    resizeMode="contain"
                  />
                </div>
              )}
            </View>

            {isCallProcessing(call, isStreamOrdering) && (
              <View className="flex w-full flex-row ml-6 mt-2">
                <Badge
                  variant="outline"
                  className="bg-amber-50 text-amber-700 border-amber-200 gap-1 !cursor-pointer"
                >
                  <StyledText>Processing...</StyledText>
                </Badge>
              </View>
            )}

            {call.order == null && call.orderEntryStatus === "SUBMITTED" && (
              <View className="flex w-full flex-row ml-6 mt-2">
                <Badge
                  variant="outline"
                  className="bg-amber-50 text-amber-700 border-amber-200 gap-1 !cursor-pointer"
                >
                  <StyledText>See POS for Order Details</StyledText>
                </Badge>
              </View>
            )}

            {hasOrder && (
              <>
                <div className="mt-3 bg-green-50 p-3 rounded-md">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium text-green-700">
                      Order #{orderId}
                    </div>
                    {call.order && call.order.total && (
                      <span className="text-sm text-green-700 font-medium">
                        {displayPrice(call.order.total)}
                      </span>
                    )}
                  </div>

                  {isExpanded ? (
                    <View className="space-y-4 mt-2">
                      {call.order?.items.map((item, index) => (
                        <View
                          key={index}
                          className="grid grid-cols-[auto_1fr_auto] gap-2"
                        >
                          <StyledText className="font-medium text-xs text-green-700">
                            {item.quantity}x{" "}
                          </StyledText>
                          <View>
                            <StyledText className="text-xs text-green-700">
                              {item.name}
                            </StyledText>
                            {renderModifiers(item.modifiers)}
                            {!!item.specialInstructions && (
                              <StyledText className="text-xs text-green-700">
                                “{item.specialInstructions}”
                              </StyledText>
                            )}
                          </View>
                        </View>
                      ))}
                    </View>
                  ) : (
                    <View className="mt-2">
                      {call.order?.items.slice(0, 1).map((item, index) => (
                        <View
                          key={index}
                          className="grid grid-cols-[auto_1fr_auto] gap-2"
                        >
                          <StyledText className="font-medium text-xs text-green-700">
                            {item.quantity}x{" "}
                          </StyledText>
                          <StyledText className="text-xs text-green-700">
                            {item.name}
                          </StyledText>
                        </View>
                      ))}
                      {(call.order?.items.length || 0) > 1 && (
                        <StyledText className="text-green-600 mt-1 text-xs">
                          + {(call.order?.items.length || 0) - 1} more{" "}
                          {(call.order?.items.length || 0) - 1 === 1
                            ? "item"
                            : "items"}
                        </StyledText>
                      )}
                    </View>
                  )}
                </div>

                {isExpanded ? (
                  <View className="flex flex-col gap-2 mt-3">
                    <View className="flex flex-row justify-between items-end">
                      <View className="flex flex-row gap-2 mx-1">
                        {getFulfillmentBadge()}
                        {getTimingBadge(isExpanded)}
                      </View>
                      {hasOrder && isPrepaid(call) && (
                        <Pressable
                          onPress={onRefund}
                          className={`group h-7 px-2 flex-row items-center justify-center rounded ${"active:bg-red-50 hover:bg-red-50"}`}
                        >
                          <HandCoins
                            size={12}
                            className={`mr-1 group-hover:text-red-700 text-gray-600`}
                          />
                          <StyledText className="text-xs text-gray-600 active:text-red-700 group-hover:text-red-700">
                            Refund
                          </StyledText>
                        </Pressable>
                      )}
                    </View>
                    {isDelivery && (
                      <Pressable className="flex flex-row items-center gap-1 p-2">
                        <MapPin size={12} color="#7e22ce" />
                        <StyledText className="text-xs font-medium text-start text-purple-700 select-text">
                          {formattedDeliveryAddress}
                        </StyledText>
                      </Pressable>
                    )}
                    {isScheduled && call.order?.futureOrderDate != null && (
                      <View className="flex flex-row items-center gap-1 p-2">
                        <Calendar size={12} color="#374151" />
                        <StyledText className="text-xs font-medium text-start text-gray-700 underline underline-offset-2 decoration-dotted">
                          {formatDateTime(call.order?.futureOrderDate)}
                        </StyledText>
                      </View>
                    )}
                  </View>
                ) : (
                  <div className="flex items-center gap-2 mt-3 px-1">
                    {getFulfillmentBadge()}
                    {getTimingBadge(isExpanded)}
                  </div>
                )}

                {isExpanded && hasBeenRefunded(call) && (
                  <View className="flex w-full flex-row mt-2">
                    <View className="flex-row items-center gap-2 p-2 rounded-md border border-orange-200">
                      <HandCoins size={16} color="#fb923c" />
                      <StyledText className="text-orange-700 text-xs">
                        Caller has been refunded
                      </StyledText>
                    </View>
                  </View>
                )}

                {isExpanded && isExpired(call) && (
                  <View className="mt-1">
                    <View className="flex-row items-center gap-2 p-3 bg-red-50 rounded-md border border-red-200">
                      <CircleX size={20} color="#dc2626" />
                      <StyledText className="text-red-700 font-medium text-xs">
                        Since the payment was not completed, the order has been
                        cancelled.
                      </StyledText>
                    </View>
                  </View>
                )}

                {isExpanded && isVoided(call) && (
                  <View className="mt-1">
                    <View className="flex-row items-center gap-2 p-3 bg-red-50 rounded-md border border-red-200">
                      <CircleX size={20} color="#dc2626" />
                      <StyledText className="text-red-700 font-medium text-xs">
                        This order has been voided.
                      </StyledText>
                    </View>
                  </View>
                )}
              </>
            )}
          </Pressable>

          {isExpanded && (
            <View className="grid grid-cols-2 gap-2 my-3 items-center">
              <Pressable
                className="px-1.5 h-8 rounded-md border flex-row items-center justify-center border-border shadow-sm bg-white hover:bg-gray-100 gap-2"
                onPress={onCallBack}
                accessibilityRole="link"
                accessibilityLabel={`Call ${formatPhoneNumber(call.phone)}`}
              >
                {/* <Feather name="phone" size={16} color="#71717A" /> */}
                <StyledText className="text-gray-700">Call Back</StyledText>
              </Pressable>
              <Pressable
                className="px-1.5 h-8 rounded-md border flex-row items-center justify-center border-border shadow-sm bg-white hover:bg-gray-100 gap-2"
                onPress={onViewHistory}
              >
                {/* <Feather name="list" size={16} color="#71717A" /> */}
                <StyledText className="text-gray-700">
                  Caller History
                </StyledText>
              </Pressable>
            </View>
          )}

          {isExpanded && (
            <View className="flex flex-col gap-2 mt-3">
              <StyledText
                className={`text-muted-foreground leading-relaxed rounded-lg mb-2 ${
                  call.summary == null ? "italic" : ""
                }`}
              >
                {call.summary || "No conversation summary available"}
              </StyledText>
              <View className="flex gap-2">
                <PlayAudioButton
                  isPlaying={soundId === call.id && isPlaying}
                  onPress={() => handlePlayPause(call.audioUrl, call.id)}
                  disabled={!call.audioUrl}
                  duration={call.duration}
                  isLoading={loadingId === call.id}
                />
                <Pressable
                  className="flex-shrink-0 px-6 h-10 rounded-lg border flex-row items-center justify-center border-border shadow-sm bg-white hover:bg-gray-100"
                  onPress={onReportCall}
                >
                  <Flag size={16} className="mr-2 text-gray-700" />
                  <StyledText className="text-gray-700">Report Call</StyledText>
                </Pressable>
                <Pressable
                  className="flex-shrink-0 px-6 h-10 rounded-lg border flex-row items-center justify-center border-border shadow-sm bg-white hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  onPress={() => {
                    if (isCallerBlocked(call.phone)) {
                      onUnblockCaller(call.phone);
                    } else {
                      onBlockCaller(call.phone);
                    }
                  }}
                  disabled={isBlockUnblockLoading(call.phone)}
                >
                  {isBlockUnblockLoading(call.phone) ? (
                    <Loader2
                      size={16}
                      className="mr-2 text-gray-700 animate-spin [transform-origin:center]"
                    />
                  ) : isCallerBlocked(call.phone) ? (
                    <CircleCheck size={16} className="mr-2 text-gray-700" />
                  ) : (
                    <Ban size={16} className="mr-2 text-gray-700" />
                  )}
                  <StyledText className="text-gray-700">
                    {isBlockUnblockLoading(call.phone)
                      ? "Loading..."
                      : isCallerBlocked(call.phone)
                        ? "Unblock Caller"
                        : "Block Caller"}
                  </StyledText>
                </Pressable>
              </View>
            </View>
          )}
        </div>
      </View>
    </View>
  );
};

export default CallCard;
